import { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  Search,
  Close as CloseIcon,
  ExpandMore,
  Help,
  AutoAwesome,
  Biotech,
  Category,
  DirectionsCar,
  Grass,
  Light,
  LocationCity,
  Person,
  PrecisionManufacturing,
  SmartToy,
  SolarPower,
  Square,
  Videocam,
  Flight,
  Chair,
  Tag,
  PushPin,
  PushPinOutlined,
} from "@mui/icons-material";
import { Model } from "../../models/Model";
import { Drone, Ship } from "lucide-react";
import { RenderAccordionToolbar } from "../../components/Minibar";

const categoryIcons: Record<string, JSX.Element> = {
  Buildings: <LocationCity sx={{ fontSize: "1.75rem" }} />,
  Cameras: <Videocam sx={{ fontSize: "1.75rem" }} />,
  Features: <AutoAwesome sx={{ fontSize: "1.75rem" }} />,
  People: <Person sx={{ fontSize: "1.75rem" }} />,
  Lights: <Light sx={{ fontSize: "1.75rem" }} />,
  Machines: <Biotech sx={{ fontSize: "1.75rem" }} />,
  Plants: <Grass sx={{ fontSize: "1.75rem" }} />,
  Props: <Category sx={{ fontSize: "1.75rem" }} />,
  Robots: <PrecisionManufacturing sx={{ fontSize: "1.75rem" }} />,
  Solar: <SolarPower sx={{ fontSize: "1.75rem" }} />,
  Test: <SmartToy sx={{ fontSize: "1.75rem" }} />,
  Vehicles: <DirectionsCar sx={{ fontSize: "1.75rem" }} />,
  Walls: <Square sx={{ fontSize: "1.75rem" }} />,
  Drones: <Drone size={24} />,
  Furniture: <Chair sx={{ fontSize: "1.6rem" }} />,
  Attributes: <Tag sx={{ fontSize: "1.75rem" }} />,
  Aircraft: <Flight sx={{ fontSize: "1.75rem" }} />,
  Ships: <Ship size={24} />,
  Office: <LocationCity sx={{ fontSize: "1.75rem" }} />,
};

interface ModelSelectBarProps {
  models: Model[];
  selectedModelId: string;
  onModelSelect: (model: Model) => void;
}

export function ModelSelectBar({
  models,
  selectedModelId,
  onModelSelect,
}: ModelSelectBarProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState<string | false>(false);
  const [pinnedCategories, setPinnedCategories] = useState<Set<string>>(
    new Set()
  );

  const filteredModels = models.filter((model) =>
    model.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedModels = filteredModels.reduce((acc, model) => {
    const category = model.category || "Uncategorized";
    acc[category] = acc[category] || [];
    acc[category].push(model);
    return acc;
  }, {} as Record<string, Model[]>);

  const handleModelSelect = (model: Model) => {
    onModelSelect(model);
  };

  const clearSearch = () => setSearchQuery("");

  const toggleExpand = (category: string) => {
    setExpanded((prev) => (prev === category ? false : category));
  };

  const togglePin = (category: string) => {
    setPinnedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const isOpen = (category: string) =>
    expanded === category || pinnedCategories.has(category);

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: "#212121",
        display: "flex",
        flexDirection: "column",

        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.mode === "dark" ? "#444" : "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.mode === "dark" ? "#222" : "#ddd",
        },
      }}
    >
      {/* Search Field */}
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          width: "100%",
          paddingBottom: "8px",
          paddingLeft: "8px",
          paddingRight: "8px",

          paddingTop: "8px",
          "& .MuiOutlinedInput-root": {
            height: "17px",
            fontSize: "12px",
            backgroundColor: "#212121",
            color: "#fff",
            borderRadius: "3",
            "& fieldset": { borderColor: "#444" },
          },
          "& input": { padding: "6px 8px" },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: "#fff" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  edge="end"
                  sx={{ color: "#fff" }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Model Categories */}
      <Box sx={{ p: 0, m: 0 }}>
        {Object.entries(groupedModels).length > 0 ? (
          Object.entries(groupedModels)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, models]) => (
              <Accordion
                key={category}
                disableGutters
                expanded={isOpen(category)}
                sx={{
                  boxShadow: "none",
                  "&::before": { display: "none" },
                  m: 0,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  onClick={() => toggleExpand(category)}
                  sx={{
                    backgroundColor: "#1E1E1E",
                    color: "white",
                    px: "0.75rem",
                    minHeight: "0px !important",
                    "& .MuiAccordionSummary-content": {
                      m: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  {categoryIcons[category] || (
                    <Help sx={{ fontSize: "1rem", mr: "0.5rem" }} />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      mt: "2.5px",
                      padding: "7px",
                      flexGrow: 1,
                    }}
                  >
                    {category}
                  </Typography>

                  {/* Pin button */}
                  {/* <IconButton
                    size="small"
                    sx={{ color: "#fff", padding: 0, marginLeft: "8px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(category);
                    }}
                  >
                    {pinnedCategories.has(category) ? (
                      <PushPin sx={{ fontSize: "14px" }} />
                    ) : (
                      <PushPinOutlined sx={{ fontSize: "14px" }} />
                    )}
                  </IconButton> */}
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <RenderAccordionToolbar
                    pinned={pinnedCategories}
                    panel={category}
                    togglePin={togglePin}
                  />
                  {models.map((model) => (
                    <MenuItem
                      key={model._id}
                      sx={{
                        py: 0.5,
                        fontSize: "0.875rem",
                        backgroundColor:
                          model._id === selectedModelId
                            ? theme.palette.action.selected
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            model._id === selectedModelId
                              ? theme.palette.action.selected
                              : theme.palette.action.hover,
                        },
                      }}
                      onClick={() => handleModelSelect(model)}
                    >
                      {model.name}
                    </MenuItem>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))
        ) : (
          <MenuItem disabled sx={{ p: "8px 16px", fontStyle: "italic" }}>
            No models found
          </MenuItem>
        )}
      </Box>
    </Box>
  );
}
